import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TradePlanPreviewInput } from 'shared';

import { PositionSizer } from '../components/domain/PositionSizer';
import { RewardRiskMeter } from '../components/domain/RewardRiskMeter';
import { RulesGateResult } from '../components/domain/RulesGateResult';
import { TopStatusBar } from '../components/domain/TopStatusBar';
import { TradePlanForm, type TradePlanDraft } from '../components/domain/TradePlanForm';
import { TradePlanRegimeBanner } from '../components/domain/TradePlanRegimeBanner';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAccounts } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import { usePendingReviews } from '../features/reviews/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';
import { useCreateTradePlan } from '../features/tradePlans/mutations';
import { useTradePlanPreview } from '../features/tradePlans/queries';

export function TradePlan() {
  const me = useMe();
  const accounts = useAccounts();
  const createPlan = useCreateTradePlan();
  const [draft, setDraft] = useState<TradePlanDraft>({});
  const [debouncedDraft, setDebouncedDraft] = useState<TradePlanDraft>({});
  const swingAccounts = useMemo(
    () => (accounts.data ?? []).filter((account) => account.mandate === 'SWING_TRADING'),
    [accounts.data],
  );
  const activeAccountId = swingAccounts.some((account) => account.id === draft.accountId)
    ? (draft.accountId ?? null)
    : (swingAccounts[0]?.id ?? null);
  const snapshot = useLatestSnapshot(activeAccountId);
  const pendingReviews = usePendingReviews();
  const draftSymbol = draft.symbol?.toUpperCase();
  const pendingReview =
    pendingReviews.data?.items.find((item) => item.symbol === draftSymbol) ?? null;

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedDraft(draft), 300);
    return () => window.clearTimeout(handle);
  }, [draft]);
  const previewInput = useMemo(
    () => toPreviewInput(debouncedDraft, activeAccountId),
    [activeAccountId, debouncedDraft],
  );
  const preview = useTradePlanPreview(previewInput);

  if (me.isLoading || accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading trade plan...</main>;
  }

  if (me.isError || accounts.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load trade plan.</main>;
  }

  if (swingAccounts.length === 0) {
    return (
      <main className="min-h-screen bg-bg text-text">
        <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
        <div className="mx-auto max-w-3xl px-4 py-8">
          <Card>
            <h1 className="text-xl font-semibold">No Swing Trading accounts are configured.</h1>
            <p className="mt-2 text-sm text-text-muted">
              TradePlans are available only for accounts with the Swing Trading mandate.
            </p>
            <Button asChild className="mt-4">
              <Link to="/settings">Open Settings</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  const nav = snapshot.data ? Number(snapshot.data.nav) : undefined;
  const decision = preview.data?.gateDecision ?? null;
  const requiresOverride = decision?.decision === 'WARN';
  const overrideProvided = (draft.overrideReason?.trim().length ?? 0) >= 5;
  const gateDisablesSubmit =
    decision === null || decision.decision === 'BLOCKED' || (requiresOverride && !overrideProvided);
  const rewardRisk = liveRewardRisk(draft);
  const profitLadder = liveProfitTakingLadder(debouncedDraft, decision?.recommendedQty);
  const plannedEntryRange = livePlannedEntryRange(debouncedDraft);

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto max-w-7xl space-y-4 px-4 pt-6">
        <TradePlanRegimeBanner regime={preview.data?.regime ?? null} />
      </div>
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <Card
          header={
            <div>
              <h1 className="text-2xl font-semibold">Trade Plan</h1>
              <p className="text-sm text-text-muted">
                Every plan goes through the gate before execution. Quantity is set automatically
                from the stop and current risk limits.
              </p>
            </div>
          }
        >
          {pendingReview ? (
            <div className="mb-4 rounded border border-warn/60 bg-warn-subtle p-3 text-sm text-amber-100">
              You have a pending review for the last {pendingReview.symbol} trade. Submit it to
              sharpen your plan.{' '}
              <Link className="font-semibold underline" to={`/reviews/${pendingReview.tradeId}`}>
                Review now
              </Link>
            </div>
          ) : null}
          <TradePlanForm
            accounts={swingAccounts}
            isSubmitting={createPlan.isPending}
            disabled={gateDisablesSubmit}
            requiresOverride={requiresOverride}
            {...(decision?.recommendedQty !== undefined
              ? { recommendedQty: decision.recommendedQty }
              : {})}
            onChange={setDraft}
            onSubmit={(input) => createPlan.mutate(input)}
          />
        </Card>
        <div className="hidden space-y-4 lg:block">
          <RulesGateResult decision={decision} isLoading={preview.isFetching} />
          <PositionSizer
            {...(debouncedDraft.proposedEntry !== undefined
              ? { entry: debouncedDraft.proposedEntry }
              : {})}
            {...(debouncedDraft.proposedStop !== undefined
              ? { stop: debouncedDraft.proposedStop }
              : {})}
            {...(nav !== undefined ? { nav } : {})}
            {...(decision?.recommendedQty !== undefined
              ? { recommendedQty: decision.recommendedQty }
              : {})}
            {...(decision?.maxByStop !== undefined ? { maxByStop: decision.maxByStop } : {})}
            {...(decision?.maxBySize !== undefined ? { maxBySize: decision.maxBySize } : {})}
            {...(preview.data?.regime.riskTierPct !== undefined
              ? { maxRiskPct: preview.data.regime.riskTierPct }
              : {})}
          />
          <Card
            header={
              <div>
                <h2 className="text-lg font-semibold">Standard swing profit contract</h2>
                <p className="text-sm text-text-muted">
                  Locked before entry: 20% at 1.5R, 25% at 2.5R, 25% at 3.5R, then run the remaining
                  whole shares.
                </p>
              </div>
            }
          >
            {plannedEntryRange ? (
              <div className="mb-4 flex items-center justify-between gap-3 border-b border-border pb-3 text-sm">
                <span className="text-text-muted">Planned entry range</span>
                <span className="font-mono tabular-nums">
                  {plannedEntryRange.minimum.toFixed(2)} - {plannedEntryRange.maximum.toFixed(2)}
                </span>
              </div>
            ) : null}
            {profitLadder.length === 0 ? (
              <p className="text-sm text-text-muted">
                Enter a valid entry and stop to lock the ladder.
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                {profitLadder.map((step) => (
                  <div
                    key={step.label}
                    className="grid grid-cols-[70px_1fr_auto] items-center gap-3"
                  >
                    <span className="font-semibold">{step.label}</span>
                    <span className="text-text-muted">
                      Exit {step.exitPct}%{step.exitQty !== null ? ` (${step.exitQty} shares)` : ''}
                    </span>
                    <span className="font-mono tabular-nums">
                      {step.targetPrice === null ? 'Trail stop' : step.targetPrice.toFixed(2)}
                    </span>
                  </div>
                ))}
                <p className="pt-2 text-xs text-text-muted">
                  After TP2, move the stop to at least breakeven. Stops may tighten, never widen.
                </p>
              </div>
            )}
          </Card>
          <RewardRiskMeter rewardRisk={rewardRisk} />
        </div>
      </div>
      <div className="fixed inset-x-3 bottom-20 z-30 lg:hidden">
        <RulesGateResult decision={decision} isLoading={preview.isFetching} />
      </div>
    </main>
  );
}

function livePlannedEntryRange(draft: TradePlanDraft): { minimum: number; maximum: number } | null {
  const minimum = draft.plannedEntryMin;
  const maximum = draft.plannedEntryMax;
  if (
    minimum === undefined ||
    maximum === undefined ||
    !Number.isFinite(minimum) ||
    !Number.isFinite(maximum) ||
    minimum <= 0 ||
    maximum <= 0 ||
    minimum > maximum
  ) {
    return null;
  }
  return { minimum, maximum };
}

function toPreviewInput(
  draft: TradePlanDraft,
  accountId: string | null,
): TradePlanPreviewInput | null {
  if (
    !accountId ||
    !draft.symbol ||
    !/^[A-Z0-9.-]+$/.test(draft.symbol) ||
    draft.proposedEntry === undefined ||
    !Number.isFinite(draft.proposedEntry) ||
    draft.proposedEntry <= 0
  ) {
    return null;
  }
  return {
    accountId,
    symbol: draft.symbol,
    side: draft.side ?? 'BUY',
    proposedEntry: draft.proposedEntry,
    ...(draft.proposedStop !== undefined ? { proposedStop: draft.proposedStop } : {}),
    ...(draft.proposedTarget !== undefined ? { proposedTarget: draft.proposedTarget } : {}),
    ...(draft.proposedQty !== undefined ? { proposedQty: draft.proposedQty } : {}),
    ...(draft.theme !== undefined ? { theme: draft.theme } : {}),
    tradeType: draft.tradeType ?? 'SWING',
    conviction: draft.conviction ?? 2,
    thesis: draft.thesis ?? '',
    ...(draft.invalidation !== undefined ? { invalidation: draft.invalidation } : {}),
    isFtdPilot: draft.isFtdPilot ?? false,
  };
}

function liveRewardRisk(draft: TradePlanDraft): number | null {
  const { proposedEntry: entry, proposedStop: stop, proposedTarget: target } = draft;
  if (entry === undefined || stop === undefined || target === undefined) return null;
  const risk = draft.side === 'SELL' ? stop - entry : entry - stop;
  const reward = draft.side === 'SELL' ? entry - target : target - entry;
  if (risk <= 0 || reward < 0) return null;
  return reward / risk;
}

function liveProfitTakingLadder(draft: TradePlanDraft, recommendedQty?: number) {
  const entry = draft.proposedEntry;
  const stop = draft.proposedStop;
  if (entry === undefined || stop === undefined) return [];
  const risk = Math.abs(entry - stop);
  if (!Number.isFinite(risk) || risk <= 0) return [];
  const direction = draft.side === 'SELL' ? -1 : 1;
  const qty = recommendedQty !== undefined ? Math.max(0, Math.floor(recommendedQty)) : null;
  const tp1Qty = qty === null ? null : Math.floor(qty * 0.2);
  const tp2Qty = qty === null ? null : Math.floor(qty * 0.25);
  const tp3Qty = qty === null ? null : Math.floor(qty * 0.25);
  const runnerQty =
    qty === null || tp1Qty === null || tp2Qty === null || tp3Qty === null
      ? null
      : qty - tp1Qty - tp2Qty - tp3Qty;
  return [
    { label: 'TP1', exitPct: 20, exitQty: tp1Qty, targetPrice: entry + direction * risk * 1.5 },
    { label: 'TP2', exitPct: 25, exitQty: tp2Qty, targetPrice: entry + direction * risk * 2.5 },
    { label: 'TP3', exitPct: 25, exitQty: tp3Qty, targetPrice: entry + direction * risk * 3.5 },
    { label: 'RUNNER', exitPct: 30, exitQty: runnerQty, targetPrice: null },
  ];
}
